import { useEffect, useMemo, useState } from "react";
import styles from "./index.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { pdf } from "@react-pdf/renderer";
import PdfDocumentDfAiSummary from "../../layouts/PdfDocumentDfAiSummary";
import { aiAxios } from "../../../utils/axios/ai.axios";
import { categorySteps } from "../../../utils/constant";
import { convert_usd_to_nzd } from "../../../utils/conversion";
import { fetchDashboardAllDataAsyncThunk } from "../../../store/slices/Products/dashboard.slice";
import { filterLocationsByOrg } from "../../../utils/helpers";
import {
  parseDfAiSummaryBlocks,
  stripDfSummaryReportFooter,
  stripInlineBoldMarkers,
} from "../../../utils/dfAiSummaryParse";

const DfAiSummary = () => {
  const dispatch = useDispatch();
  const { source, location: availableLocations = [], loadingLocation, currency } = useSelector(
    (store) => store.constant
  );
  const organizationId = useSelector((store) => store.user.organizationId);
  const selectedLocations = useSelector((store) => store.dashboard?.selLocation || []);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [summaryText, setSummaryText] = useState("");
  const [loading, setLoading] = useState(false);

  const generatedDate = useMemo(() => {
    return new Date().toLocaleDateString();
  }, [summaryText]);

  const sanitizeSummaryText = (text) => stripDfSummaryReportFooter(text);

  const effectiveLocations = useMemo(() => {
    if (selectedLocations.length > 0) return selectedLocations;
    if (!Array.isArray(availableLocations)) return [];
    return filterLocationsByOrg(availableLocations, organizationId);
  }, [selectedLocations, availableLocations, organizationId]);

  const loadSummary = async () => {
    if (!source || loadingLocation) return;
    try {
      const category = queryParams.get("category");
      const sub_category = queryParams.get("sub_category");
      const brand = queryParams.get("brand");
      const locationParam = queryParams.get("location");
      const pricerank = queryParams.get("pricerank");
      const pricerange = queryParams.get("pricerange");

      const defaultCategory = categorySteps.join(",").toLowerCase();
      const defaultLocation = effectiveLocations.length
        ? `${effectiveLocations.join(",").toLowerCase()},${source}`
        : source;

      const filters = {
        source,
        force: false,
        category: category || defaultCategory,
        sub_category: sub_category ? decodeURIComponent(sub_category) : null,
        brand,
        location: locationParam || defaultLocation,
        pricerank,
        pricerange,
      };

      setLoading(true);
      const res = await aiAxios.post("/df-products-summary", filters);
      const sanitized = sanitizeSummaryText(res?.data || "");
      setSummaryText(sanitized);
    } catch (e) {
      toast.error("Failed to load AI summary.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!source || loadingLocation) return;
    dispatch(
      fetchDashboardAllDataAsyncThunk({
        source,
        offset: 0,
        limit: 10,
        category: categorySteps.join(",").toLowerCase(),
        brand: null,
        location: effectiveLocations.length
          ? `${effectiveLocations.join(",").toLowerCase()},${source}`
          : source,
      })
    );
  }, [dispatch, source, loadingLocation, effectiveLocations]);

  useEffect(() => {
    loadSummary();
  }, [source, loadingLocation, effectiveLocations, location.search]);

  const handleDownload = async () => {
    try {
      if (!summaryText) {
        toast.error("AI summary is not available yet.");
        return;
      }
      const doc = (
        <PdfDocumentDfAiSummary source={source} summary={sanitizeSummaryText(summaryText)} />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `df-ai-summary-${source}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error("Failed to download PDF.");
    }
  };

  const isMonetaryColumn = (header) => {
    const h = String(header || "").toLowerCase();
    if (!h) return false;
    if (h.includes("%")) return false;
    return h.includes("price") || h.includes("avg") || h.includes("average") || h.includes("gap");
  };

  const formatPriceCell = (value, header) => {
    const raw = stripInlineBoldMarkers(String(value ?? "")).trim();
    if (!raw || raw === "—" || /^n\/a$/i.test(raw) || raw.includes("%")) return raw;
    if (!isMonetaryColumn(header)) return raw.replace(/NZ\$/gi, "$");

    const numericText = raw.replace(/^NZ\$/i, "").replace(/^\$/, "").replace(/,/g, "");
    const parsed = Number(numericText);
    if (!Number.isFinite(parsed)) return raw.replace(/NZ\$/gi, "$");

    const converted = convert_usd_to_nzd(parsed, currency || "NZD");
    const convertedNum = Number(converted);
    if (!Number.isFinite(convertedNum)) return raw.replace(/NZ\$/gi, "$");
    return `$${convertedNum.toFixed(2)}`;
  };

  const renderBlock = (block, idx) => {
    if (block.type === "heading") {
      return (
        <p key={`hd-${idx}`} className={styles.sectionHeading}>
          {stripInlineBoldMarkers(block.text)}
        </p>
      );
    }
    if (block.type === "subheading") {
      return (
        <p key={`sh-${idx}`} className={styles.subheading}>
          {stripInlineBoldMarkers(block.text)}
        </p>
      );
    }
    if (block.type === "bullets") {
      return (
        <ul key={`bl-${idx}`} className={styles.bulletList}>
          {block.items.map((item, j) => (
            <li key={`li-${idx}-${j}`}>{stripInlineBoldMarkers(item)}</li>
          ))}
        </ul>
      );
    }
    if (block.type === "footer") {
      return null;
    }
    if (block.type === "table") {
      let header = block.rows[0] || [];
      const wowIdx = header.findIndex((h) => /wow/i.test(String(h)));
      if (wowIdx !== -1) {
        header = header.filter((_, i2) => i2 !== wowIdx);
      }
      const body = block.rows
        .slice(1)
        .map((r) => (wowIdx !== -1 ? r.filter((_, i2) => i2 !== wowIdx) : r));
      return (
        <div key={`tbl-wrap-${idx}`} className={styles.tableWrap}>
          {block.brief ? (
            <p className={styles.tableBrief}>{stripInlineBoldMarkers(block.brief)}</p>
          ) : null}
          <table className={styles.table}>
          <thead>
            <tr>
              {header.map((h, hIdx) => (
                <th key={`h-${hIdx}`}>{stripInlineBoldMarkers(String(h))}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, rIdx) => (
              <tr key={`r-${rIdx}`}>
                {r.map((c, cIdx) => (
                  <td key={`c-${cIdx}`}>{formatPriceCell(c, header[cIdx])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      );
    }
    return (
      <p key={`ln-${idx}`} className={styles.paragraph}>
        {stripInlineBoldMarkers(block.text || "\u00A0")}
      </p>
    );
  };

  return (
    <div className={styles["container"]}>
      <div className={styles["paper"]}>
        <span className={styles.betaTag}>BETA Version</span>
        <div className={styles["headingRow"]}>
          <div className={styles["heading"]}>AI Summary</div>
          <div className={styles["actions"]}>
            <button type="button" onClick={handleDownload} disabled={loading || !summaryText}>
              Download
            </button>
          </div>
        </div>
        <div className={styles["meta"]}>Report generated as of {generatedDate}</div>
        <div className={styles["body"]}>
          {loading && <div className={styles.loading}>Loading AI summary...</div>}
          {!loading && !summaryText && <div className={styles.empty}>No AI summary available.</div>}
          {!loading && summaryText && parseDfAiSummaryBlocks(summaryText).map(renderBlock)}
        </div>
      </div>
    </div>
  );
};

export default DfAiSummary;
